import { formationAPI, spaEntityAPI } from '../services/api';
import { fetchOrganizationSettings } from '../services/settingsAPI';
import { fetchFormationEnrollmentBundle } from './formationEnrollmentBundle';

const PAYMENT_METHOD_FROM_API = {
  cash: 'Espèces',
  card: 'Carte bancaire',
  transfer: 'Virement bancaire',
  check: 'Chèque',
};

/** Same normalization as Payments page — one source of truth for amounts/dates. */
export function normalizeFormationPayment(row) {
  if (!row) return row;
  let legacy = {};
  if (row.legacyPayloadJson) {
    try {
      legacy =
        typeof row.legacyPayloadJson === 'string'
          ? JSON.parse(row.legacyPayloadJson)
          : row.legacyPayloadJson;
    } catch {
      /* ignore */
    }
  }
  return {
    ...legacy,
    ...row,
    isPaid: row.isPaid === true || row.isPaid === 1,
    method:
      legacy.method ||
      PAYMENT_METHOD_FROM_API[row.paymentMethod] ||
      row.paymentMethod ||
      row.method,
    reference: legacy.reference || row.invoiceNumber || row.reference,
    paymentDate: row.date || legacy.paymentDate || legacy.date,
    date: row.date || legacy.date,
    totalAmount:
      row.totalAmount != null ? Number(row.totalAmount) : Number(legacy.totalAmount) || 0,
    paidAmount:
      row.paidAmount != null ? Number(row.paidAmount) : Number(legacy.paidAmount) || 0,
    amount: row.amount != null ? Number(row.amount) : Number(legacy.amount) || 0,
    courseId: row.courseId || legacy.courseId || '',
    batchId: row.batchId || legacy.batchId || '',
    studentId: row.studentId || legacy.studentId || '',
    student: legacy.student || row.student,
    course: legacy.course || row.course,
    batch: legacy.batch || row.batch,
  };
}

export async function fetchBenefitsPayments() {
  const res = await formationAPI.getFormationPayments({});
  const items = Array.isArray(res?.items) ? res.items : [];
  return items.map(normalizeFormationPayment);
}

export function getPaymentDate(payment) {
  const raw = payment?.date || payment?.paymentDate || payment?.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function getPaidAmount(payment) {
  const paid = Number(payment?.paidAmount ?? payment?.amount ?? 0);
  return Number.isFinite(paid) ? paid : 0;
}

export function getTotalAmount(payment) {
  const total = Number(payment?.totalAmount ?? payment?.amount ?? 0);
  return Number.isFinite(total) ? total : 0;
}

export function isCountedPayment(payment) {
  if (!payment) return false;
  if (payment.isPaid) return true;
  if (payment.status === 'paid' || payment.status === 'partial') return true;
  return getPaidAmount(payment) > 0;
}

export function filterPaymentsInRange(payments, rangeStart, rangeEnd) {
  return (payments || []).filter((payment) => {
    const d = getPaymentDate(payment);
    return d && d >= rangeStart && d <= rangeEnd;
  });
}

export function filterPaymentsByMonth(payments, year, month1to12) {
  const start = new Date(year, month1to12 - 1, 1);
  const end = new Date(year, month1to12, 0, 23, 59, 59, 999);
  return filterPaymentsInRange(payments, start, end);
}

export function filterExpensesInRange(expenses, rangeStart, rangeEnd) {
  return (expenses || []).filter((expense) => {
    const d = new Date(expense.date || expense.createdAt);
    return !Number.isNaN(d.getTime()) && d >= rangeStart && d <= rangeEnd;
  });
}

export function sumPaidRevenue(payments) {
  return (payments || []).reduce((sum, payment) => {
    if (!isCountedPayment(payment)) return sum;
    return sum + getPaidAmount(payment);
  }, 0);
}

export function sumTeacherCommission(payments, commissionRate = 0.4) {
  return (payments || []).reduce((sum, payment) => {
    if (!isCountedPayment(payment)) return sum;
    return sum + getPaidAmount(payment) * commissionRate;
  }, 0);
}

export function sumExpenseAmounts(expenses) {
  return (expenses || []).reduce((sum, expense) => {
    const amount = Number(expense.amount ?? 0);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

export function computePeriodFinancials(
  payments,
  expenses,
  rangeStart,
  rangeEnd,
  commissionRate = 0.4
) {
  const periodPayments = filterPaymentsInRange(payments, rangeStart, rangeEnd);
  const periodExpenses = filterExpensesInRange(expenses, rangeStart, rangeEnd);
  const totalIncome = sumPaidRevenue(periodPayments);
  const teacherCosts = sumTeacherCommission(periodPayments, commissionRate);
  const educationalExpenses = sumExpenseAmounts(periodExpenses);
  const totalExpenses = teacherCosts + educationalExpenses;
  const netProfit = totalIncome - totalExpenses;
  const profitMargin = totalIncome > 0 ? (netProfit / totalIncome) * 100 : 0;

  return {
    totalIncome,
    teacherCosts,
    educationalExpenses,
    totalExpenses,
    netProfit,
    profitMargin,
    paymentsCount: periodPayments.length,
    expensesCount: periodExpenses.length,
    periodPayments,
    periodExpenses,
  };
}

/** Load all entities used across Bénéfices tabs (shared with other pages). */
export async function fetchBenefitsDashboardData() {
  const [payments, courses, trainers, expenses, bundle, settings] = await Promise.all([
    fetchBenefitsPayments(),
    spaEntityAPI.list('courses'),
    spaEntityAPI.list('trainers'),
    spaEntityAPI.list('expenses'),
    fetchFormationEnrollmentBundle(),
    fetchOrganizationSettings().catch(() => ({})),
  ]);

  const activeStudents = new Set(
    (bundle.enrollments || [])
      .filter((e) => e.status === 'active' && e.studentId)
      .map((e) => e.studentId)
  ).size;

  const activeCourses = (courses || []).filter(
    (c) => c.status === 'active' || !c.status
  ).length;

  const commissionRate =
    settings?.teacherCommissionRate != null
      ? Number(settings.teacherCommissionRate)
      : 0.4;

  return {
    payments,
    courses: courses || [],
    trainers: trainers || [],
    expenses: expenses || [],
    batches: bundle?.batches || [],
    enrollments: bundle?.enrollments || [],
    activeStudents,
    activeCourses,
    commissionRate: Number.isFinite(commissionRate) ? commissionRate : 0.4,
    settings: settings || {},
  };
}

/** Resolve course ids taught by a trainer (course assignment + batch assignment). */
export function courseIdsForTrainer(trainerId, courses, batches) {
  const ids = new Set();
  for (const course of courses || []) {
    if (
      course.trainer?.id === trainerId ||
      course.instructorId === trainerId ||
      course.trainerId === trainerId
    ) {
      ids.add(course.id);
    }
  }
  for (const batch of batches || []) {
    if (batch.trainerId === trainerId && batch.courseId) {
      ids.add(batch.courseId);
    }
  }
  return ids;
}

export function paymentBelongsToTrainer(payment, trainerId, courses, batches) {
  if (!payment || !trainerId) return false;
  const courseIds = courseIdsForTrainer(trainerId, courses, batches);
  if (payment.courseId && courseIds.has(payment.courseId)) return true;
  if (payment.batchId) {
    const batch = (batches || []).find((b) => b.id === payment.batchId);
    if (batch?.trainerId === trainerId) return true;
    if (batch?.courseId && courseIds.has(batch.courseId)) return true;
  }
  const course = (courses || []).find((c) => c.id === payment.courseId);
  return (
    course?.trainer?.id === trainerId ||
    course?.instructorId === trainerId ||
    course?.trainerId === trainerId
  );
}

export function computeTrainerEarnings(
  payments,
  courses,
  trainers,
  batches,
  commissionRate = 0.4
) {
  const earnings = {};
  for (const trainer of trainers || []) {
    earnings[trainer.id] = 0;
    for (const payment of payments || []) {
      if (!isCountedPayment(payment)) continue;
      if (!paymentBelongsToTrainer(payment, trainer.id, courses, batches)) continue;
      earnings[trainer.id] += getPaidAmount(payment) * commissionRate;
    }
  }
  return earnings;
}

export function resolveStudentLabel(payment, studentsById = {}) {
  if (payment?.student?.name) return payment.student.name;
  const sid = payment?.studentId;
  if (sid && studentsById[sid]) {
    const s = studentsById[sid];
    return s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || sid;
  }
  return payment?.studentName || sid || '—';
}

/** Active student + course counts for a trainer (enrollment bundle + assignments). */
export function computeTrainerEnrollmentStats(trainerId, courses, batches, enrollments) {
  const courseIds = courseIdsForTrainer(trainerId, courses, batches);
  const batchIds = new Set();
  for (const batch of batches || []) {
    if (batch.trainerId === trainerId) batchIds.add(batch.id);
    else if (batch.courseId && courseIds.has(batch.courseId)) batchIds.add(batch.id);
  }
  const students = new Set();
  for (const enrollment of enrollments || []) {
    if (enrollment.status === 'active' && enrollment.studentId && batchIds.has(enrollment.batchId)) {
      students.add(enrollment.studentId);
    }
  }
  return { courses: courseIds.size, students: students.size };
}

/** Per-trainer commission breakdown from formation payments. */
export function computeTrainerPaymentDetails(
  payments,
  courses,
  trainers,
  batches,
  commissionRate = 0.4,
  studentsById = {}
) {
  const details = {};
  for (const trainer of trainers || []) {
    details[trainer.id] = {
      paymentCommissions: [],
      totalCommissions: 0,
      relatedPayments: [],
    };
    for (const payment of payments || []) {
      if (!isCountedPayment(payment)) continue;
      if (!paymentBelongsToTrainer(payment, trainer.id, courses, batches)) continue;
      const paidAmount = getPaidAmount(payment);
      const commission = paidAmount * commissionRate;
      const course = (courses || []).find((c) => c.id === payment.courseId);
      details[trainer.id].paymentCommissions.push({
        paymentId: payment.id,
        reference: payment.reference,
        studentName: resolveStudentLabel(payment, studentsById),
        courseName: course?.title || payment.course?.title || '—',
        amount: paidAmount,
        commission,
        date: payment.paymentDate || payment.createdAt,
        status: payment.status,
      });
      details[trainer.id].totalCommissions += commission;
      details[trainer.id].relatedPayments.push(payment);
    }
  }
  return details;
}
